/**
 * Panneau debug: voir {@link ../utils/isAppDebug} et {@link ../components/DebugConfigPanel}.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { fetchConfig } from "./configUtils";
import { isAppDebug } from "../utils/isAppDebug";

const ConfigContext = createContext(null);

export const DEBUG_CONFIG_SUBDOMAIN_STORAGE_KEY = "debug_config_subdomain";

const isDebugMode = () => isAppDebug();

function readPersistedDebugSubdomain() {
  if (!isDebugMode() || typeof sessionStorage === "undefined") return null;
  const v = sessionStorage.getItem(DEBUG_CONFIG_SUBDOMAIN_STORAGE_KEY);
  const t = v?.trim();
  return t ? t : null;
}

function ConfigLoadNotice({ error, onRetry }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center text-slate-800">
      <p className="max-w-md text-sm">
        {error ||
          "Configuration indisponible. Vérifiez le service ou réessayez."}
      </p>
      {onRetry ? (
        <button
          type="button"
          className="rounded-lg bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700"
          onClick={onRetry}
        >
          Réessayer
        </button>
      ) : null}
    </div>
  );
}

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [configRefreshing, setConfigRefreshing] = useState(false);

  const reloadConfig = useCallback(async (subdomainOverride) => {
    const trimmed =
      typeof subdomainOverride === "string" ? subdomainOverride.trim() : "";
    if (trimmed === "") {
      if (isDebugMode()) {
        sessionStorage.removeItem(DEBUG_CONFIG_SUBDOMAIN_STORAGE_KEY);
      }
      setConfigRefreshing(true);
      try {
        const next = await fetchConfig();
        setConfig(next);
        setLoadError(null);
      } catch (e) {
        const msg =
          e?.message || "Échec du chargement de la configuration (n8n).";
        setLoadError(msg);
        throw e;
      } finally {
        setConfigRefreshing(false);
      }
      return;
    }
    if (isDebugMode()) {
      sessionStorage.setItem(DEBUG_CONFIG_SUBDOMAIN_STORAGE_KEY, trimmed);
    }
    setConfigRefreshing(true);
    try {
      const next = await fetchConfig({ subdomainOverride: trimmed });
      setConfig(next);
      setLoadError(null);
    } catch (e) {
      const msg =
        e?.message || "Échec du chargement de la configuration (n8n).";
      setLoadError(msg);
      throw e;
    } finally {
      setConfigRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const persisted = readPersistedDebugSubdomain();
    const promise = persisted
      ? fetchConfig({ subdomainOverride: persisted })
      : fetchConfig();
    promise
      .then((c) => {
        setConfig(c);
        setLoadError(null);
      })
      .catch((e) => {
        setLoadError(
          e?.message || "Échec du chargement de la configuration (n8n).",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  if (!config) {
    if (loading) return null;
    return (
      <ConfigLoadNotice
        error={loadError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <ConfigContext.Provider
      value={{ config, reloadConfig, configRefreshing }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const ctx = useContext(ConfigContext);
  return ctx?.config;
};

export const useConfigDebug = () => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    return {
      reloadConfig: async () => {},
      configRefreshing: false,
    };
  }
  return {
    reloadConfig: ctx.reloadConfig,
    configRefreshing: ctx.configRefreshing,
  };
};
